// Text folding for catalog search (CHT-710). A search document and a search query are folded by the
// same function, so a query typed without accents finds a name written with them. Folding runs in the
// application, not in Postgres: `unaccent()` is STABLE, and a generated column needs IMMUTABLE.
/**
 * The version of the folding rules. It is part of `SEARCH_DOCUMENT_VERSION`: a change to the rules
 * changes every stored document, so it needs a new document version and a rebuild.
 */
export const FOLD_VERSION = 1;
// Latin ligatures and letters that Unicode does not decompose. Everything else that matters for Latin
// text is a base letter plus a combining mark.
const LETTER_MAP = Object.freeze({
    ß: "ss",
    æ: "ae",
    œ: "oe",
    ø: "o",
    ł: "l",
    đ: "d",
});
// Only the Combining Diacritical Marks block. Devanagari vowel signs and Japanese voicing marks are
// outside it, so `ताज` and `ガ` keep their meaning.
const COMBINING_MARKS = /[̀-ͯ]/g;
const UNFOLDED_LETTERS = /[ßæœøłđ]/g;
/**
 * Folds text for search: NFKD, drop Latin combining marks, NFC, lowercase, map the letters that do not
 * decompose (`ß`, `æ`, `œ`, `ø`, `ł`, `đ`), collapse white space and trim. It is pure, total and
 * idempotent. It keeps non-Latin scripts as they are (`東京`, `ताज`).
 */
export function foldSearchText(value) {
    return value
        .normalize("NFKD")
        .replace(COMBINING_MARKS, "")
        .normalize("NFC")
        .toLowerCase()
        .replace(UNFOLDED_LETTERS, (letter) => LETTER_MAP[letter])
        .replace(/\s+/gu, " ")
        .trim();
}
// --- tokens ---------------------------------------------------------------------------------------------
/**
 * Words that carry no search meaning, in English and a few French and Italian articles that appear in
 * venue names. They are dropped from a query's tokens and from the fake embedder's features. They stay in
 * a stored name (a name match compares the whole folded name). Folded, lowercase, frozen.
 */
export const SEARCH_STOP_WORDS = Object.freeze(new Set([
    "a",
    "an",
    "and",
    "are",
    "as",
    "at",
    "be",
    "by",
    "for",
    "from",
    "in",
    "is",
    "it",
    "of",
    "on",
    "or",
    "the",
    "to",
    "with",
    "de",
    "du",
    "des",
    "la",
    "le",
    "les",
    "l",
    "d",
    "un",
    "une",
    "et",
    "il",
    "di",
    "del",
    "della",
    "s",
]));
const TOKEN = /[\p{L}\p{N}]+/gu;
/**
 * The word tokens of folded text: runs of letters and digits, so an apostrophe or a hyphen separates two
 * tokens (`l'epoque` is `l`, `epoque`). The same runs a Postgres `simple` text search configuration
 * keeps, which makes a query token and a stored token comparable. Run it on `foldSearchText` output.
 */
export function searchTokens(folded) {
    return folded.match(TOKEN) ?? [];
}
